/**
 * useQAACheck Hook
 * Checks QAA contract status for a CMO name against Status Verträge.xlsx
 */

import { useState, useEffect, useCallback } from 'react';
import { BRRApiClient } from '../services';
import { QAACheckResponse } from '../services/types';

interface UseQAACheckOptions {
  cmoName: string | null;
  apiClient: BRRApiClient;
  autoLoad?: boolean;
}

interface UseQAACheckReturn {
  data: QAACheckResponse | null;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

export const useQAACheck = ({
  cmoName,
  apiClient,
  autoLoad = true
}: UseQAACheckOptions): UseQAACheckReturn => {
  const [data, setData] = useState<QAACheckResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!cmoName || !cmoName.trim()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await apiClient.checkCmoQAA(cmoName.trim());
      setData(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      console.error('Failed to check QAA status:', error);
    } finally {
      setLoading(false);
    }
  }, [cmoName, apiClient]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  useEffect(() => {
    if (autoLoad && cmoName && cmoName.trim()) {
      void load();
    }
  }, [cmoName, autoLoad, load]);

  return {
    data,
    loading,
    error,
    reload
  };
};
